import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, ArrowLeft, MapPin, Building2, Calendar, Home,
  TrendingUp, Wrench, Users, Target, CheckCircle2, DollarSign,
  BarChart3, Lightbulb, ChevronRight, Shield, Clock, Layers
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { trackRecord, calendlyLink } from '../../data/mock';
import { CaseStudySchema } from '../../components/StructuredData';

const JarmeseApartmentsPage = () => {
  // Get Jarmese property data from trackRecord
  const property = trackRecord.find(p => p.name.includes('Jarmese')) || trackRecord[0];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Property details
  const propertyDetails = {
    name: 'Jarmese Apartments',
    altName: 'Anais Place',
    location: '4835-4840 Jarmese St, Houston, TX 77033',
    submarket: 'South Houston / Greater Third Ward',
    county: 'Harris County',
    units: 72,
    assetClass: 'Multifamily - Workforce Housing',
    acquisitionYear: 2014,
    status: 'Active',
    image: property.image
  };

  // Value-add improvements
  const improvements = [
    { title: 'Unit Interior Renovations', description: 'Updated flooring, fixtures, and appliances across targeted units' },
    { title: 'Exterior Improvements', description: 'Enhanced curb appeal with landscaping and building facade updates' },
    { title: 'Common Area Upgrades', description: 'Improved amenity spaces and community areas' },
    { title: 'Security Enhancements', description: 'Upgraded lighting, access control, and surveillance systems' },
    { title: 'Operational Efficiencies', description: 'Implemented institutional-grade property management practices' },
    { title: 'Utility Optimization', description: 'Water conservation measures and energy efficiency improvements' }
  ];

  // Investment thesis points
  const investmentThesis = [
    { title: 'Below-Market Acquisition', description: 'Acquired at significant discount due to deferred maintenance and management inefficiencies' },
    { title: 'Strong Submarket Fundamentals', description: 'Location benefits from proximity to employment centers and transportation corridors' },
    { title: 'Workforce Housing Demand', description: 'Consistent tenant demand for affordable, quality housing in Greater Houston' },
    { title: 'Value-Add Opportunity', description: 'Clear path to increase NOI through renovations and operational improvements' }
  ];

  // Operational highlights
  const operationalHighlights = [
    { metric: 'Occupancy Stabilization', description: 'Improved occupancy through targeted marketing and tenant screening' },
    { metric: 'Rent Growth', description: 'Achieved rental rate increases following unit renovations' },
    { metric: 'Expense Control', description: 'Reduced operating costs through vendor renegotiations and efficiency measures' },
    { metric: 'Tenant Retention', description: 'Enhanced resident satisfaction leading to improved lease renewals' }
  ];

  // Lessons learned
  const lessonsLearned = [
    'Workforce housing continues to demonstrate resilient demand across economic cycles',
    'In-house property management provides critical operational control for value execution',
    'Phased renovation approach allows for testing improvements before full deployment',
    'South Houston submarkets offer compelling risk-adjusted returns relative to core urban areas'
  ];

  // Other case studies for navigation
  const otherCaseStudies = trackRecord.filter(p => !p.name.includes('Jarmese')).slice(0, 3);

  return (
    <>
      <Helmet>
        <title>Jarmese Apartments Case Study | Houston Multifamily Investment | ASAP Capital</title>
        <meta 
          name="description" 
          content="Value-add multifamily investment case study of Jarmese Apartments in Houston, detailing acquisition strategy, renovations, and operational improvements." 
        />
        <meta name="keywords" content="Jarmese Apartments, Houston multifamily investment, value add case study, apartment repositioning, workforce housing Houston, Texas multifamily investment" />
        <link rel="canonical" href="/case-studies/jarmese-apartments" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-32 pb-0 lg:pt-40 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              {/* Breadcrumb */}
              <motion.div 
                className="flex items-center gap-2 text-sm mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Link to="/case-studies" className="text-white/50 hover:text-[#D4AF37] transition-colors">
                  Case Studies
                </Link>
                <ChevronRight className="h-4 w-4 text-white/30" />
                <span className="text-white/70">Jarmese Apartments</span>
              </motion.div>

              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-10 items-end"
              >
                {/* Left - Content */}
                <div className="pb-10 lg:pb-16">
                  <motion.div 
                    className="flex items-center gap-3 mb-4"
                    variants={itemVariants}
                  >
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                      propertyDetails.status === 'Active' 
                        ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                        : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                    }`}>
                      {propertyDetails.status}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-white/10 text-white/70 text-xs border border-white/10">
                      {propertyDetails.assetClass}
                    </span>
                  </motion.div>

                  <motion.h1 
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-2"
                    variants={itemVariants}
                  >
                    Jarmese Apartments
                  </motion.h1>

                  <motion.p 
                    className="text-lg text-[#D4AF37] mb-6"
                    variants={itemVariants}
                  >
                    Multifamily Investment Case Study
                  </motion.p>

                  <motion.div 
                    className="flex flex-wrap gap-6 text-white/60 text-sm mb-8"
                    variants={itemVariants}
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#D4AF37]" />
                      <span>Houston, TX</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Home className="h-4 w-4 text-[#D4AF37]" />
                      <span>{propertyDetails.units} Units</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-[#D4AF37]" />
                      <span>Acquired {propertyDetails.acquisitionYear}</span>
                    </div>
                  </motion.div>

                  <motion.p 
                    className="text-white/50 text-sm italic border-l-2 border-[#D4AF37]/30 pl-4"
                    variants={itemVariants}
                  >
                    Past performance is not indicative of future results. Investment opportunities are offered only through a Private Placement Memorandum to investors.
                  </motion.p>
                </div>

                {/* Right - Image */}
                <motion.div 
                  className="relative"
                  variants={itemVariants}
                >
                  <div className="relative rounded-t-2xl lg:rounded-2xl overflow-hidden">
                    <img 
                      src={propertyDetails.image}
                      alt={propertyDetails.name}
                      className="w-full aspect-[4/3] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 via-transparent to-transparent" />
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== PROPERTY OVERVIEW ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-8" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                    <Building2 className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Property Overview
                  </h2>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { label: 'Property Name', value: propertyDetails.name, subValue: `(${propertyDetails.altName})`, icon: Building2 },
                    { label: 'Location', value: 'Houston, TX 77033', subValue: propertyDetails.submarket, icon: MapPin },
                    { label: 'Unit Count', value: `${propertyDetails.units} Units`, subValue: propertyDetails.assetClass, icon: Home },
                    { label: 'Acquisition', value: propertyDetails.acquisitionYear, subValue: `Status: ${propertyDetails.status}`, icon: Calendar }
                  ].map((item, index) => (
                    <motion.div
                      key={item.label}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10"
                      variants={itemVariants}
                    >
                      <item.icon className="h-6 w-6 text-[#D4AF37] mb-3" />
                      <p className="text-white/40 text-xs mb-1">{item.label}</p>
                      <p className="text-white font-bold text-lg">{item.value}</p>
                      {item.subValue && <p className="text-white/50 text-sm">{item.subValue}</p>}
                    </motion.div>
                  ))}
                </div>

                <motion.div 
                  className="mt-8 p-5 bg-white/5 rounded-xl border border-white/5"
                  variants={itemVariants}
                >
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-white/70 font-medium mb-1">Full Address</p>
                      <p className="text-white/50 text-sm">{propertyDetails.location}</p>
                      <p className="text-white/40 text-sm mt-1">{propertyDetails.county}, Greater Houston Metro Area</p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== INVESTMENT THESIS ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                    <Target className="h-5 w-5 text-blue-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Investment Thesis and Acquisition Strategy
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  Jarmese Apartments represented a compelling value-add opportunity in Houston's workforce 
                  housing segment. The property was acquired below replacement cost with clear operational 
                  upside potential through targeted renovations and institutional management practices.
                </motion.p>

                <div className="grid md:grid-cols-2 gap-6">
                  {investmentThesis.map((item, index) => (
                    <motion.div
                      key={item.title}
                      className="flex items-start gap-4 p-5 bg-gradient-to-r from-[#111D35] to-transparent rounded-xl border border-white/5 hover:border-blue-500/30 transition-all"
                      variants={itemVariants}
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 border border-blue-500/20">
                        <CheckCircle2 className="h-5 w-5 text-blue-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                        <p className="text-white/50 text-sm">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== VALUE-ADD BUSINESS PLAN ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                    <Wrench className="h-5 w-5 text-amber-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Value-Add Business Plan
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  The renovation program focused on strategic improvements designed to enhance resident 
                  experience, improve property operations, and position the asset competitively within 
                  the South Houston submarket.
                </motion.p>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {improvements.map((item, index) => (
                    <motion.div
                      key={item.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-white/10 hover:border-amber-500/30 transition-all"
                      variants={itemVariants}
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center mb-3">
                        <span className="text-amber-400 font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                      </div>
                      <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                      <p className="text-white/50 text-sm">{item.description}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== EXECUTION AND IMPROVEMENTS ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                    <Layers className="h-5 w-5 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Execution and Property Improvements
                  </h2>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-10">
                  <motion.div variants={itemVariants}>
                    <h4 className="text-white font-semibold mb-4">Renovation Timeline</h4>
                    <div className="space-y-4">
                      {[
                        { phase: 'Phase 1', title: 'Property Assessment', description: 'Comprehensive evaluation of deferred maintenance and improvement opportunities' },
                        { phase: 'Phase 2', title: 'Unit Renovations', description: 'Targeted interior upgrades to vacant units with highest rent growth potential' },
                        { phase: 'Phase 3', title: 'Common Areas', description: 'Exterior improvements and amenity space enhancements' },
                        { phase: 'Phase 4', title: 'Ongoing Optimization', description: 'Continued operational improvements and turnover renovations' }
                      ].map((item, index) => (
                        <div key={item.phase} className="flex items-start gap-4">
                          <span className="text-emerald-400 font-bold text-sm mt-1">{item.phase}</span>
                          <div>
                            <p className="text-white font-medium">{item.title}</p>
                            <p className="text-white/50 text-sm">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <h4 className="text-white font-semibold mb-4">Leasing Initiatives</h4>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-6 border border-white/10">
                      <ul className="space-y-3">
                        {[
                          'Enhanced marketing presence and online listings',
                          'Improved tenant screening and qualification processes',
                          'Competitive pricing strategy aligned with renovated units',
                          'Community engagement programs for resident retention',
                          'Responsive maintenance protocols to improve satisfaction'
                        ].map((item, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                            <span className="text-white/60 text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== OPERATIONAL PERFORMANCE ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                    <BarChart3 className="h-5 w-5 text-purple-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Operational Performance and Stabilization
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  Through disciplined execution of the business plan, the property has achieved meaningful 
                  improvements in operational metrics. The following highlights represent historical 
                  performance indicators.
                </motion.p>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {operationalHighlights.map((item, index) => (
                    <motion.div
                      key={item.metric}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-white/10 text-center"
                      variants={itemVariants}
                      whileHover={{ y: -5, borderColor: 'rgba(168, 85, 247, 0.3)' }}
                    >
                      <h4 className="text-white font-semibold mb-2">{item.metric}</h4>
                      <p className="text-white/50 text-sm">{item.description}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Disclaimer */}
                <motion.div 
                  className="bg-white/[0.02] border border-white/5 rounded-xl p-4"
                  variants={itemVariants}
                >
                  <p className="text-white/40 text-xs text-center">
                    <Shield className="h-4 w-4 inline-block mr-2 text-white/30" />
                    Performance metrics shown are historical and do not guarantee future results. 
                    All investments involve risk. Please refer to offering documents for complete details.
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== LESSONS LEARNED ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                    <Lightbulb className="h-5 w-5 text-cyan-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Lessons Learned and Market Insights
                  </h2>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-6">
                  {lessonsLearned.map((lesson, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start gap-4 p-5 bg-gradient-to-r from-[#111D35] to-transparent rounded-xl border border-white/5"
                      variants={itemVariants}
                    >
                      <div className="w-8 h-8 rounded-full bg-cyan-500/10 flex items-center justify-center flex-shrink-0 border border-cyan-500/20">
                        <span className="text-cyan-400 font-bold text-sm">{index + 1}</span>
                      </div>
                      <p className="text-white/60">{lesson}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== STRATEGY FIT ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                <motion.div variants={itemVariants}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                      <TrendingUp className="h-5 w-5 text-[#D4AF37]" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      How This Investment Fits Our Strategy
                    </h2>
                  </div>

                  <p className="text-white/60 mb-6">
                    Jarmese Apartments exemplifies our value-add investment approach in the Houston market. 
                    By acquiring assets below replacement cost and implementing operational improvements, 
                    we seek to generate attractive risk-adjusted returns while providing quality workforce 
                    housing to the Greater Houston community.
                  </p>

                  <p className="text-white/60 mb-6">
                    This investment demonstrates our ability to identify opportunities, execute renovation 
                    programs, and manage properties through our vertically integrated platform.
                  </p>

                  <div className="flex flex-wrap gap-3">
                    <Link to="/strategy">
                      <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold rounded-full">
                        View Investment Strategy
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/portfolio">
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full">
                        View Portfolio
                      </Button>
                    </Link>
                  </div>
                </motion.div>

                <motion.div 
                  className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10"
                  variants={itemVariants}
                >
                  <h4 className="text-white font-semibold mb-6">Our Value-Add Approach</h4>
                  <div className="space-y-4">
                    {[
                      { title: 'Acquire Below Market', description: 'Target distressed or mismanaged assets at attractive basis' },
                      { title: 'Execute Improvements', description: 'Implement strategic renovations and operational upgrades' },
                      { title: 'Optimize Operations', description: 'Apply institutional management practices to improve NOI' },
                      { title: 'Create Value', description: 'Position assets for long-term appreciation and stable cash flow' }
                    ].map((item, index) => (
                      <div key={item.title} className="flex items-start gap-4">
                        <span className="text-[#D4AF37] font-bold">{String(index + 1).padStart(2, '0')}</span>
                        <div>
                          <p className="text-white font-medium">{item.title}</p>
                          <p className="text-white/50 text-sm">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== EXPLORE MORE CTA ==================== */}
          <section className="py-20 lg:py-28 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                  Explore More Investment Case Studies
                </h2>
                <p className="text-white/50 max-w-2xl mx-auto">
                  Review additional examples of our value-add investment approach across Houston's 
                  multifamily and commercial real estate markets.
                </p>
              </motion.div>

              {/* Other Case Studies */}
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {otherCaseStudies.map((study) => (
                  <motion.div
                    key={study.id}
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl border border-white/10 overflow-hidden hover:border-[#D4AF37]/30 transition-all group"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img 
                        src={study.image} 
                        alt={study.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <p className="text-[#D4AF37] text-sm mb-1">{study.type}</p>
                      <h4 className="text-white font-semibold mb-2 group-hover:text-[#D4AF37] transition-colors">
                        {study.name}
                      </h4>
                      <p className="text-white/50 text-sm">{study.location}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* CTA Buttons */}
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <Link to="/case-studies">
                  <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-8 py-3 h-12 rounded-full shadow-lg shadow-[#D4AF37]/20">
                    View All Case Studies
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/track-record">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-3 h-12 rounded-full">
                    View Track Record
                  </Button>
                </Link>
                <motion.a 
                  href={calendlyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-3 h-12 rounded-full">
                    Schedule Consultation
                    <Calendar className="ml-2 h-4 w-4" />
                  </Button>
                </motion.a>
              </motion.div>
            </div>
          </section>

          {/* ==================== BACK LINK ==================== */}
          <section className="py-8 relative border-t border-white/5">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <Link 
                to="/case-studies"
                className="inline-flex items-center gap-2 text-white/50 hover:text-[#D4AF37] transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to All Case Studies
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default JarmeseApartmentsPage;
